import { useSortable } from '@dnd-kit/sortable';
import type { VirtualItem } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { type JSX } from 'react';

import {
  DataTableV2RowContextProvider,
  useDataTableV2,
} from '../contexts/DataTableV2Context.js';
import { SubRowCellContextProvider } from '../features/SubRows/SubRowCellContext.js';
import type { Row } from '../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../public.api.js';
import { DataTableV2Cell } from './DataTableV2Cell.js';
import {
  dataTableV2BodyCSS,
  dataTableV2FirstColumnCSS,
  dataTableV2FirstRightPinnedColumnCSS,
  dataTableV2FirstUnpinnedColumnCSS,
  dataTableV2LastColumnCSS,
  dataTableV2LastLeftPinnedColumnCSS,
  dataTableV2PinnedCellInDragOverlayCSS,
} from './DataTableV2Rows.sty.js';
import { dataTableV2RowDraggingCSS } from '../features/RowOrder/RowOrder.sty.js';

/** @internal */
export function RowFragment<TData extends DataTableV2RowData>({
  virtualRow,
  rows,
  virtualColumns,
  columnMeasure,
  isInDragOverlay,
  subRowIndicatorColumnId,
  enableExpanding,
}: Readonly<{
  virtualRow: VirtualItem;
  rows: Row<TData>[];
  virtualColumns: VirtualItem[];
  isInDragOverlay: boolean;
  columnMeasure?: (node: Element | null) => void;
  subRowIndicatorColumnId?: string | null;
  enableExpanding?: boolean;
}>): JSX.Element {
  const row = rows[virtualRow.index];

  const { setNodeRef, isDragging } = useSortable({
    id: row.id,
    data: {
      row,
      rowIndex: virtualRow.index,
      virtualRow,
    },
  });

  const table = useDataTableV2();
  const prefixColumnsCount = table.getBuiltinColumnsCount();

  return (
    <DataTableV2RowContextProvider value={row as Row<unknown>}>
      {virtualColumns.map((virtualCell, cellIndex, allCells) => {
        const visibleCells = row.getVisibleCells();
        const cell = visibleCells[virtualCell.index];

        // For columns, only measure the first rendered row.
        // All other rows will have the same column widths.
        const shouldMeasureColumn = rows.at(0)?.index === virtualRow.index;
        const leftPinnedColumns = table.getLeftVisibleLeafColumns();
        const rightPinnedColumns = table.getRightVisibleLeafColumns();

        return (
          <SubRowCellContextProvider
            key={`${virtualRow.key}-${virtualCell.key}`}
            isSubRowIndicatorColumn={
              enableExpanding === true &&
              subRowIndicatorColumnId === cell.column.id
            }
            cell={cell}
          >
            <DataTableV2Cell
              cell={cell}
              // don't add these props when the cell is in the drag overlay
              {...(!isInDragOverlay && {
                columnMeasure,
                shouldMeasureColumn,
                rowIndex: virtualRow.index,
                dragAndDropRef: cellIndex === 0 ? setNodeRef : undefined,
              })}
              columnIndex={virtualCell.index}
              style={{
                ...cell.getGridPosition(
                  `${virtualCell.index}/${virtualRow.index}`,
                ),
              }}
              className={clsx({
                [dataTableV2FirstColumnCSS]: virtualCell.index === 0,
                [dataTableV2FirstUnpinnedColumnCSS]:
                  prefixColumnsCount > 0 &&
                  virtualCell.index === prefixColumnsCount,
                [dataTableV2LastColumnCSS]:
                  virtualCell.index === visibleCells.length - 1,
                [dataTableV2LastLeftPinnedColumnCSS]:
                  leftPinnedColumns.length - 1 === cell.column.getIndex(),
                [dataTableV2FirstRightPinnedColumnCSS]:
                  rightPinnedColumns.length !== 0 &&
                  rightPinnedColumns[0].id === cell.column.id,
                [dataTableV2PinnedCellInDragOverlayCSS]:
                  cell.column.getIsPinned() && isInDragOverlay,
                dataTableV2BodyCSS,
                // don't add these styles when the cell is in the drag overlay
                ...(!isInDragOverlay && {
                  [dataTableV2RowDraggingCSS.transparent]: isDragging,
                }),
              })}
              cellDeps={cell.column.columnDef?.meta
                ?.cellDeps?.(cell)
                .toString()}
            />
          </SubRowCellContextProvider>
        );
      })}
    </DataTableV2RowContextProvider>
  );
}
